import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ClockIcon,
  UsersIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  FireIcon,
  EyeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import CheckInForm from '../../components/sessions/CheckInForm';
import SessionList from '../../components/sessions/SessionList';
import SessionStats from '../../components/sessions/SessionStats';
import { mockSessions } from '../../utils/mockData';

const SessionsPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [showModal, setShowModal] = useState(false);
  const [memberCode, setMemberCode] = useState('');
  const [isCheckingIn, setIsCheckingIn] = useState(true);

  // Instead of defining mockSessions here, import it from utils/mockData

  // Fetch sessions data for overview (avec données fictives)
  const { data: sessionsData, isLoading } = useQuery('sessions', async () => {
    try {
      const response = await axios.get('/sessions/attendance');
      return response.data;
    } catch (error) {
      console.warn("Utilisation des données de test en production (échec API)");
      // Utiliser les données fictives en cas d'erreur
      return { sessions: mockSessions };
    }
  });

  // Fetch active sessions (avec données fictives)
  const { data: activeSessionsData } = useQuery('active-sessions', async () => {
    try {
      const response = await axios.get('/sessions/attendance?type=check-in');
      return response.data;
    } catch (error) {
      console.warn("Utilisation des données de test en production (échec API active sessions)");
      // Retourner uniquement les sessions actives (sans checkOutTime)
      return { 
        sessions: mockSessions.filter(session => !session.checkOutTime)
      };
    }
  });

  const sessions = sessionsData?.sessions || mockSessions;
  const activeSessions = activeSessionsData?.sessions || mockSessions.filter(session => !session.checkOutTime);

  // Calculate quick stats
  const quickStats = {
    activeSessions: activeSessions.length,
    todaySessions: sessions.filter(s => {
      const today = new Date().toDateString();
      return new Date(s.checkInTime).toDateString() === today;
    }).length,
    totalSessions: sessions.length,
    avgDuration: sessions.length > 0 ? 
      Math.round(sessions.reduce((sum, s) => {
        if (s.checkOutTime) {
          const duration = (new Date(s.checkOutTime) - new Date(s.checkInTime)) / 1000 / 60;
          return sum + duration;
        }
        return sum;
      }, 0) / sessions.filter(s => s.checkOutTime).length) : 0,
    peakHour: (() => {
      const hourCounts = {};
      sessions.forEach(s => {
        const hour = new Date(s.checkInTime).getHours();
        hourCounts[hour] = (hourCounts[hour] || 0) + 1;
      });
      const peakHour = Object.keys(hourCounts).reduce((a, b) => 
        hourCounts[a] > hourCounts[b] ? a : b, 0
      );
      return peakHour;
    })(),
  };

  const tabs = [
    { 
      id: 'checkin', 
      name: 'Check-in/out', 
      icon: ArrowRightOnRectangleIcon,
      description: 'Enregistrer les entrées et sorties'
    },
    { 
      id: 'sessions', 
      name: 'Liste des séances', 
      icon: DocumentTextIcon,
      description: 'Historique et gestion des séances'
    },
    { 
      id: 'stats', 
      name: 'Statistiques', 
      icon: ChartBarIcon,
      description: 'Analytics et rapports'
    },
  ];

  const handleSessionUpdate = () => {
    // This function will be called when a session is created/updated
    // to refresh the data in other components
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Gestion des Séances
          </h1>
          <p className="text-white/60 mt-2">
            Système de check-in/out et suivi des présences
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl px-4 py-2 border border-white/20">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-white font-medium">{quickStats.activeSessions}</span>
              <span className="text-white/60 text-sm">actives</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Séances Actives</p>
              <p className="text-2xl font-bold text-white">{quickStats.activeSessions}</p>
              <div className="flex items-center mt-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
                <span className="text-green-400 text-sm">En temps réel</span>
              </div>
            </div>
            <div className="bg-green-500/20 p-3 rounded-xl">
              <ClockIcon className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Aujourd'hui</p>
              <p className="text-2xl font-bold text-white">{quickStats.todaySessions}</p>
              <div className="flex items-center mt-1">
                <CalendarDaysIcon className="h-3 w-3 text-blue-400 mr-1" />
                <span className="text-blue-400 text-sm">séances</span>
              </div>
            </div>
            <div className="bg-blue-500/20 p-3 rounded-xl">
              <CalendarDaysIcon className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Durée Moyenne</p>
              <p className="text-2xl font-bold text-white">
                {Math.floor(quickStats.avgDuration / 60)}h {quickStats.avgDuration % 60}m
              </p>
              <div className="flex items-center mt-1">
                <ClockIcon className="h-3 w-3 text-purple-400 mr-1" />
                <span className="text-purple-400 text-sm">par séance</span>
              </div>
            </div>
            <div className="bg-purple-500/20 p-3 rounded-xl">
              <ClockIcon className="h-6 w-6 text-purple-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Heure de Pointe</p>
              <p className="text-2xl font-bold text-white">{quickStats.peakHour}:00</p>
              <div className="flex items-center mt-1">
                <FireIcon className="h-3 w-3 text-orange-400 mr-1" />
                <span className="text-orange-400 text-sm">plus fréquentée</span>
              </div>
            </div>
            <div className="bg-orange-500/20 p-3 rounded-xl">
              <FireIcon className="h-6 w-6 text-orange-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-2 border border-white/20">
        <div className="flex space-x-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-4 px-6 rounded-xl transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">{tab.name}</div>
                <div className="text-xs opacity-80">{tab.description}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'checkin' && (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-blue-500/20 p-3 rounded-xl">
                  <ArrowRightOnRectangleIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Check-in / Check-out</h2>
                  <p className="text-white/60">Enregistrer les entrées et sorties des membres</p>
                </div>
              </div>

              <CheckInForm 
                onSessionUpdate={handleSessionUpdate} 
                activeSessions={activeSessions}
              />
            </div>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-purple-500/20 p-3 rounded-xl">
                <DocumentTextIcon className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Liste des Séances</h2>
                <p className="text-white/60">Historique des entrées et sorties</p>
              </div>
            </div>

            <SessionList sessions={sessions} />
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-green-500/20 p-3 rounded-xl">
                <ChartBarIcon className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Statistiques</h2>
                <p className="text-white/60">Analyses et rapports de présence</p>
              </div>
            </div>

            <SessionStats sessions={sessions} />
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      {isLoading && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/30 mr-3"></div>
            <span className="text-white/60">Chargement des données de séances...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsPage; 